/**
 * Library web-source (URL) risk opt-in persistence (EVI-1566).
 *
 * URL-source acceptance remains in `localStorage` — it is a separate opt-in
 * from document upload (different risks). Document-upload acceptance is
 * DB-backed via `useLibraryUploadRiskAcceptance` (EVI-1698).
 *
 * All reads/writes are wrapped in try/catch because `localStorage` throws in
 * private-browsing / disabled-storage contexts (mirrors the pattern in
 * `utils/signInSessionTracking.ts`).
 */
import { LIBRARY_SOURCE_URL_RISK_ACCEPTED_KEY } from '../contracts';

/**
 * The web-source (URL) opt-in key. Kept distinct from the document-upload key
 * so accepting one does not silently waive the other — they cover different
 * risks (uploaded files vs externally-hosted web content).
 *
 * DECLARED in `../contracts` since ARCFE-1027 (evidence C) — a persisted key is
 * a frozen identity and lives in the key factory, same rule as a query key.
 * Re-exported name-intact so every importer keeps resolving; the read/write
 * helpers below live HERE rather than in `/contracts` because `/contracts` is
 * inert and these touch `window.localStorage`.
 *
 * CARVED at ARCFE-1069 (D1 slice 1) from `src/components/library/libraryUploadRisk.ts`,
 * which is now a delegate. It is the file this family's own record flagged as
 * "holds a frozen persisted identity for this family and appears in NO census
 * artifact" — it is in the census now, and here. The import above is the only
 * change to the module: `@heidi/evidence/contracts` became `../contracts`,
 * because a package must not import itself through its own published name.
 */
export { LIBRARY_SOURCE_URL_RISK_ACCEPTED_KEY };

const readFlag = (key: string): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(key) === 'true';
  } catch {
    return false;
  }
};

const writeFlag = (key: string): void => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, 'true');
  } catch {
    // Storage unavailable (private mode / disabled) — the opt-in simply
    // re-prompts on the next attempt, which is acceptable fail-safe behaviour.
  }
};

/** True once the user has accepted the web-source (URL) risks on this browser. */
export const hasAcceptedLibrarySourceUrlRisk = (): boolean =>
  readFlag(LIBRARY_SOURCE_URL_RISK_ACCEPTED_KEY);

/** Record that the user has accepted the web-source (URL) risks (persisted for good). */
export const setLibrarySourceUrlRiskAccepted = (): void =>
  writeFlag(LIBRARY_SOURCE_URL_RISK_ACCEPTED_KEY);
